import { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertTriangle, KeyRound, Ban, Edit, Activity, Calculator } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage } from '@/lib/api';
import UserFormModal from '@/components/users/UserFormModal';
import { formatDateTime } from '@/lib/format';

interface UserDetail {
  user: { id: string; code: string; role: string; fullNameEn: string; fullNameAr: string | null;
          phone: string; email: string | null; status: string; lastLoginAt: string | null;
          createdAt: string; notes: string | null; mustChangePassword: boolean };
  effectivePermissions: string[];
  businessUnitCodes: string[];
  overrides: { permissionCode: string; granted: boolean }[];
}

const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin', admin: 'Admin', accountant: 'Accountant',
  fleet_officer: 'Operations Manager', site_manager: 'Site Manager',
  construction_supervisor: 'Site Supervisor', driver: 'Driver',
  investor: 'Investor', auditor: 'Viewer',
  tv_display: 'TV Wall Display',
};

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [resetResult, setResetResult] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!id) return;
    try {
      const r = await apiClient.get<UserDetail>('/api/v1/users/' + id);
      setData(r.data);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  async function resetPassword() {
    if (!id || !data) return;
    if (!window.confirm('Reset password for ' + data.user.fullNameEn + '? They will be forced to change it on next login.')) return;
    try {
      const r = await apiClient.post<{ newPassword: string }>('/api/v1/users/' + id + '/reset-password');
      setResetResult(r.data.newPassword);
      refresh();
    } catch (err) { window.alert(extractErrorMessage(err)); }
  }

  async function disableUser() {
    if (!id || !data) return;
    if (data.user.code === 'USR-0001') return window.alert('Cannot disable USR-0001 (super admin protected).');
    if (!window.confirm('Disable ' + data.user.fullNameEn + '? They will not be able to log in.')) return;
    try {
      await apiClient.post('/api/v1/users/' + id + '/disable');
      refresh();
    } catch (err) { window.alert(extractErrorMessage(err)); }
  }

  async function applyAccountantPreset() {
    if (!id || !data) return;
    if (data.user.role === 'accountant') {
      return window.alert(data.user.fullNameEn + ' is already an accountant. The 27 default accountant permissions are already applied through the role.');
    }
    if (!window.confirm(
      'Switch ' + data.user.fullNameEn + ' to the accountant role?\n\n' +
      'This applies the 27 standard accountant permissions (payroll, invoicing, reports, ledger, audit log) ' +
      'and removes their current role-based permissions. Per-user overrides are preserved.'
    )) return;
    try {
      await apiClient.put('/api/v1/users/' + id + '/role', { role: 'accountant' });
      refresh();
    } catch (err) { window.alert(extractErrorMessage(err)); }
  }

  async function downloadResetSheet() {
    if (!id || !resetResult) return;
    const r = await apiClient.post('/api/v1/users/' + id + '/credentials-sheet.pdf', { password: resetResult }, { responseType: 'blob' });
    const url = window.URL.createObjectURL(r.data as Blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'credentials_' + data!.user.code + '_reset.pdf';
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  }

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (error || !data) return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 p-6">
      <AlertTriangle className="h-8 w-8 text-amber-500" />
      <div className="text-sm text-muted-foreground">{error ?? 'User not found'}</div>
      <Link to="/dashboard/settings/users"><Button variant="outline" size="sm">Back to users</Button></Link>
    </div>
  );

  const u = data.user;
  const isProtected = u.code === 'USR-0001';

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard/settings/users"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Users</Button></Link>
          <div className="text-sm font-medium">{u.fullNameEn} <span className="text-muted-foreground">·</span> <span className="font-mono text-xs">{u.code}</span></div>
          <div className="w-12" />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-4 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Stat label="Role" value={ROLE_LABELS[u.role] ?? u.role} />
          <Stat label="Status" value={u.status} tone={u.status === 'active' ? 'good' : 'warn'} />
          <Stat label="Last login" value={u.lastLoginAt ? formatDateTime(u.lastLoginAt) : 'never'} />
          <Stat label="Permissions" value={String(data.effectivePermissions.length)} />
        </div>

        <div className="rounded-lg border border-border p-4 space-y-2 text-sm">
          <Row label="Code" value={u.code} mono />
          <Row label="Full name" value={u.fullNameEn} />
          {u.fullNameAr && <Row label="Name (Arabic)" value={u.fullNameAr} />}
          <Row label="Phone" value={u.phone} mono />
          <Row label="Email" value={u.email ?? '—'} />
          <Row label="Business units" value={data.businessUnitCodes.length === 0 ? '—' : data.businessUnitCodes.join(', ')} />
          <Row label="Overrides set" value={String(data.overrides.length)} />
          <Row label="Must change password" value={u.mustChangePassword ? 'Yes' : 'No'} tone={u.mustChangePassword ? 'warn' : undefined} />
          {u.notes && <Row label="Notes" value={u.notes} />}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setEditOpen(true)} className="gap-2"><Edit className="h-4 w-4" /> Edit</Button>
          <Button variant="outline" onClick={resetPassword} className="gap-2"><KeyRound className="h-4 w-4" /> Reset password</Button>
          {!isProtected && u.role !== 'accountant' && (
            <Button variant="outline" onClick={applyAccountantPreset} className="gap-2" title="Switch role to accountant — applies the 27 default finance permissions">
              <Calculator className="h-4 w-4" /> Apply accountant preset
            </Button>
          )}
          {!isProtected && (
            <Button variant="destructive" onClick={disableUser} className="gap-2"><Ban className="h-4 w-4" /> Disable user</Button>
          )}
          {isProtected && <span className="text-xs text-muted-foreground self-center">(USR-0001 protected from disable)</span>}
        </div>

        <details className="rounded-lg border border-border" open>
          <summary className="cursor-pointer px-4 py-2 text-sm font-medium bg-muted/30 inline-flex items-center gap-2">
            <Activity className="h-3.5 w-3.5" /> Effective permissions ({data.effectivePermissions.length})
          </summary>
          <div className="p-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1 text-xs font-mono text-muted-foreground">
            {data.effectivePermissions.map(p => <div key={p}>{p}</div>)}
          </div>
        </details>
      </main>

      {editOpen && (
        <UserFormModal mode="edit" userId={id}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); refresh(); }}
        />
      )}

      {resetResult && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setResetResult(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-card border border-border rounded-lg shadow-2xl max-w-lg w-full p-6 space-y-4">
            <h3 className="text-lg font-medium">Password reset</h3>
            <div className="rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-800 dark:text-amber-400 p-3 text-sm">
              <strong>This password will not be shown again.</strong> Save it now or download the PDF.
            </div>
            <div className="space-y-2">
              <div className="text-xs text-muted-foreground">New temporary password</div>
              <code className="block font-mono text-base bg-muted rounded px-3 py-2 select-all break-all">{resetResult}</code>
            </div>
            <div className="flex gap-2">
              <Button onClick={downloadResetSheet}>Download credentials sheet (PDF)</Button>
              <Button variant="ghost" onClick={() => setResetResult(null)}>Done</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'good' | 'warn' }) {
  const cls = tone === 'good' ? 'text-emerald-600 dark:text-emerald-400' : tone === 'warn' ? 'text-amber-600 dark:text-amber-400' : '';
  return (
    <div className="bg-muted rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={'text-base font-medium mt-0.5 ' + cls}>{value}</div>
    </div>
  );
}

function Row({ label, value, mono, tone }: { label: string; value: string; mono?: boolean; tone?: 'warn' }) {
  const cls = tone === 'warn' ? 'text-amber-600 dark:text-amber-400' : '';
  return (
    <div className="flex items-baseline justify-between gap-2 text-sm">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={'text-right ' + (mono ? 'font-mono ' : '') + cls}>{value}</span>
    </div>
  );
}
